import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUsage } from "../contexts/UsageContext";
import { BarChart3, AlertCircle } from "lucide-react";

const UsageIndicator = ({ userRole }) => {
  const { t } = useTranslation();
  const { usageData, isLimitReached } = useUsage();
  const [showTooltip, setShowTooltip] = useState(false);
  const isDemo = userRole === "demo";

  // Calculate color based on usage percentage
  const getTextColor = () => {
    const percentage = usageData.percentage_used;
    if (percentage >= 90) return "text-red-600";
    if (percentage >= 75) return "text-orange-600";
    if (percentage >= 50) return "text-yellow-600";
    return "text-gray-600";
  };

  const getProgressBarColor = () => {
    const percentage = usageData.percentage_used;
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 75) return "bg-orange-500";
    if (percentage >= 50) return "bg-yellow-500";
    return "bg-blue-500";
  };

  // Format reset date
  const formatResetDate = () => {
    if (!usageData.next_reset_date) return "";
    return new Date(usageData.next_reset_date).toLocaleDateString();
  };

  return (
    <div
      className="relative flex items-center"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Compact indicator */}
      <div
        className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 rounded-lg transition-colors cursor-pointer ${
          isDemo
            ? "bg-amber-50 hover:bg-amber-100"
            : "bg-gray-50 hover:bg-gray-100"
        }`}
      >
        <BarChart3
          className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${isDemo ? "text-amber-600" : getTextColor()}`}
        />
        <div className="hidden sm:flex flex-col">
          <span
            className={`text-xs font-medium ${isDemo ? "text-amber-600" : getTextColor()}`}
          >
            {usageData.current_usage}/{usageData.limit}
            {isDemo && (
              <span className="ml-1 text-amber-500 text-[10px]">DEMO</span>
            )}
          </span>
          {/* Mini progress bar */}
          <div
            className={`w-16 rounded-full h-1 mt-0.5 ${isDemo ? "bg-amber-200" : "bg-gray-200"}`}
          >
            <div
              className={`h-1 rounded-full transition-all duration-300 ${isDemo ? "bg-amber-500" : getProgressBarColor()}`}
              style={{
                width: `${Math.min(usageData.percentage_used, 100)}%`,
              }}
            />
          </div>
        </div>
        {/* Mobile: Just show the percentage */}
        <span
          className={`text-xs font-medium sm:hidden ${isDemo ? "text-amber-600" : getTextColor()}`}
        >
          {usageData.percentage_used.toFixed(0)}%
          {isDemo && <span className="ml-1 text-[10px]">D</span>}
        </span>
        {isLimitReached && (
          <AlertCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-red-500" />
        )}
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute top-full right-0 mt-2 w-48 p-3 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="text-xs space-y-2">
            <div className="font-semibold text-gray-900 flex items-center justify-between">
              {t("usage.monthlySearches", "Monthly Searches")}
              {isDemo && (
                <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
                  DEMO
                </span>
              )}
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t("usage.used", "Used")}:</span>
              <span className={`font-medium ${getTextColor()}`}>
                {usageData.current_usage} (
                {usageData.percentage_used.toFixed(0)}%)
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">
                {t("usage.remaining", "Remaining")}:
              </span>
              <span className="font-medium">
                {usageData.limit - usageData.current_usage}
              </span>
            </div>
            {usageData.next_reset_date && (
              <div className="pt-2 border-t border-gray-100">
                <span className="text-gray-500">
                  {t("usage.resetsOn", { date: formatResetDate() })}
                </span>
              </div>
            )}
            {isLimitReached && (
              <div className="pt-2 border-t border-gray-100">
                <span className="text-red-600 font-medium">
                  {t("usage.limitReached", "Monthly limit reached")}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageIndicator;
